import type { ReactNode } from 'react';

/**
 * The app's icon vocabulary: stroke-based, on a 24px grid, drawn in
 * `currentColor` so an icon always matches the text it sits next to.
 *
 * Every icon used to be written out as a full `<svg>` at its call site —
 * fourteen of them across two files, each repeating the same seven
 * attributes, and the same check mark pasted three times in a row. What
 * differs between two icons is the path and how heavy the stroke is; that is
 * all a caller should have to say.
 *
 * Decorative by default: these sit beside their own label everywhere they are
 * used, so a screen reader announcing them again would only repeat it.
 */
interface StrokeIconProps {
  /** Rendered size in pixels, square. */
  size?: number;
  strokeWidth?: number;
  className?: string;
  children: ReactNode;
}

function StrokeIcon({ size = 16, strokeWidth = 2, className, children }: StrokeIconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

type IconProps = Omit<StrokeIconProps, 'children'>;

export function CheckIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={2.6} {...props}>
      <path d="M4 12.5l5 5L20 6.5" />
    </StrokeIcon>
  );
}

export function CrossIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={2.2} {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </StrokeIcon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={2.4} {...props}>
      <path d="M12 5v14M5 12h14" />
    </StrokeIcon>
  );
}

/** A pair of scales: a point where two claimed values disagree. */
export function ScaleIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={2.2} {...props}>
      <path d="M12 4v16M5 8h14" />
      <path d="M7 8l-3 6h6zM17 8l-3 6h6z" />
    </StrokeIcon>
  );
}

/** A clock: a settled answer that has gone out of date and wants re-checking. */
export function ClockIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </StrokeIcon>
  );
}

export function QuestionIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2a2.9 2.9 0 015.6 1c0 1.9-2.8 2.3-2.8 4" />
      <path d="M12 17.4h.01" />
    </StrokeIcon>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={1.75} {...props}>
      <path d="M15 5l-7 7 7 7" />
    </StrokeIcon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={2.25} {...props}>
      <path d="M6 9l6 6 6-6" />
    </StrokeIcon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={1.75} {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0115 0" />
    </StrokeIcon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={1.75} {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 6.5L12 13l8.5-6.5" />
    </StrokeIcon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <StrokeIcon strokeWidth={1.75} {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 018 0v3" />
    </StrokeIcon>
  );
}
